import type { CSSProperties } from 'react';
import { ChevronDown, HandHeart } from 'lucide-react';
import { Badge, Button, Card, CardContent, Collapsible, CollapsibleContent, CollapsibleTrigger, cn } from '@design-system';
import { durationInWords, type CareScenarioSummary } from '@shared/consumerPlan';
import { CARE_ENVIRONMENT_LABELS, CARE_PHASE_COPY, CARE_SETTING_COPY } from '../lib/copy';
import { hoursPerWeek } from '../lib/format';
import { Figure, MetricGrid, metricFigureClassName } from './figures';

interface CareScenarioCardProps {
  scenario: CareScenarioSummary;
  isCustom: boolean;
}

// Lightest first: care usually grows heavier from one stage to the next.
const STAGE_TONES = ['bg-primary/30', 'bg-primary/60', 'bg-primary'];

function exampleActivities(activities: string, reason: string) {
  const example = activities.charAt(0).toLowerCase() + activities.slice(1);
  return `Often, assistance with ${example}. ${reason}`;
}

const monthsLabel = (months: number) => `${Math.round(months)} months`;

/**
 * Each stage with where it happens and how much care it takes. From tablet width up, the stages share the figures' three
 * columns so each sits under its heading; on phones the bar is one strip sized by each stage's months.
 */
function StageBar({ phases }: { phases: CareScenarioSummary['phases'] }) {
  const grow = (months: number) => ({ '--stage-months': Math.max(months, 1) }) as CSSProperties;

  return (
    <div className="flex flex-col gap-3">
      <div aria-hidden className="flex h-2.5 gap-[3px] overflow-hidden rounded-full sm:grid sm:grid-cols-3 sm:gap-8 sm:overflow-visible">
        {phases.map((phase, index) => (
          <span key={phase.carePhase} style={grow(phase.months)} className={cn('grow-(--stage-months) basis-0 sm:rounded-full', STAGE_TONES[index])} />
        ))}
      </div>
      <ol aria-label="Care stages" className="flex flex-col gap-2 text-sm sm:grid sm:grid-cols-3 sm:gap-8">
        {phases.map((phase, index) => (
          <li key={phase.carePhase} className="flex min-w-0 items-start gap-2">
            <span aria-hidden className={cn('mt-1.5 size-2.5 shrink-0 rounded-full sm:hidden', STAGE_TONES[index])} />
            <span className="flex min-w-0 flex-col gap-0.5">
              <strong className="font-semibold text-gray-950">{CARE_ENVIRONMENT_LABELS[phase.careEnvironment]}</strong>
              <span className="text-muted-foreground">
                {monthsLabel(phase.months)} · {hoursPerWeek(phase.careHoursPerWeek)}
              </span>
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** Folded by default: examples of the help each stage usually brings. */
function StageHelp({ phases }: { phases: CareScenarioSummary['phases'] }) {
  return (
    <Collapsible className="group/stages rounded-lg border bg-white">
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="w-full justify-between">
          <span className="flex items-center gap-2">
            <HandHeart />
            What help looks like at each stage
          </span>
          <ChevronDown className="transition-transform duration-200 ease-out group-data-[state=open]/stages:rotate-180" />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-4 px-4 pt-3 pb-4 text-sm">
        <p className="text-muted-foreground">Examples of how needs often change, not a prediction of your exact activities.</p>
        <ol className="flex flex-col gap-4 sm:grid sm:grid-cols-3 sm:gap-8">
          {phases.map((phase, index) => {
            const phaseCopy = CARE_PHASE_COPY[phase.carePhase];
            return (
              <li key={phase.carePhase} className="flex flex-col gap-1">
                <span className="flex items-center gap-2">
                  <span aria-hidden className={cn('size-2.5 shrink-0 rounded-full', STAGE_TONES[index])} />
                  <strong className="text-gray-950">{phaseCopy.title}</strong>
                </span>
                <p className="text-gray-950">{exampleActivities(phaseCopy.activities, phaseCopy.reason)}</p>
              </li>
            );
          })}
        </ol>
      </CollapsibleContent>
    </Collapsible>
  );
}

/** The projected care scenario on one card (when, where, how much care, and each stage), with the stage details folded. */
export function CareScenarioCard({ scenario, isCustom }: CareScenarioCardProps) {
  const setting = scenario.careSetting ? CARE_SETTING_COPY[scenario.careSetting].title : 'Your chosen settings';

  return (
    <section data-testid="consumer-care-scenario" className="flex flex-col gap-6">
      <Card className="py-6">
        <CardContent className="flex flex-col gap-6 px-6">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-gray-950">
              {isCustom ? 'Your custom care scenario' : 'Your estimated care needs'}
            </h2>
            <Badge variant={isCustom ? 'outline' : 'secondary'}>{isCustom ? 'Custom scenario' : 'Our estimate'}</Badge>
          </div>
          <MetricGrid>
            <Figure label="Care starts" value={`Age ${scenario.careStartAge} · ${durationInWords(scenario.totalMonths)}`} className={metricFigureClassName} />
            <Figure label="Where" value={setting} className={metricFigureClassName} />
            <Figure label="Weekly care" value={hoursPerWeek(scenario.careHoursPerWeek)} className={metricFigureClassName} />
          </MetricGrid>
          <StageBar phases={scenario.phases} />
        </CardContent>
      </Card>
      <StageHelp phases={scenario.phases} />
    </section>
  );
}
